import React, { useState, useEffect, useRef } from 'react';
import Logo from '../../assets/images/logo-fairytale-new.png';
import LoginBg from '../../assets/images/login-bg-blue.webp';
import userIcon from '../../assets/images/user-icon.png';
import { loginResponse, getUser, searchClients } from '../../Api';
import { LAYAR_SAPA_PENDING } from '../../components/LayarSapa';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import './Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Ushers get the scanner; subscribers get read-only dashboard + history.
  const allowedRoles = ['usher', 'subscriber'];
  const skipSearchRef = useRef(false); // <--- this prevents search after selecting post

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const decodeHTML = (html) => {
    const decoded = DOMPurify.sanitize(html);
    const txt = document.createElement('textarea');
    txt.innerHTML = decoded;
    return txt.value;
  };
  const setAnimation = (key, anim) => {
    if (!anim) return;
    localStorage.setItem(
      key,
      `${anim.animation_type} ${anim.animation_delay} ${anim.animation_duration}`
    );
  };

  const handleSelectPost = (post) => {
    const decodedTitle = decodeHTML(post.title);
    skipSearchRef.current = true;
    setSelectedPost(post);
    setSearchTerm(decodedTitle);
    setSearchResults([]);
    localStorage.setItem('selectedPostId', post.id);
    localStorage.setItem('selectedPostTitle', decodedTitle);
    localStorage.setItem('selectedLayar', post.acf.layar_sapa);
    localStorage.setItem('selectedLayarNoText', post.acf.layar_sapa_tanpa_text);
    localStorage.setItem('selectedCustomer', post.customer);
    localStorage.setItem('temp_cat', post.acf.temp_cat);
    setAnimation('header_anim', post.acf.welcome_anim);
    setAnimation('subtitle_anim', post.acf.subtitle_anim);
    setAnimation('guest_anim', post.acf.guest_anim);
    setAnimation('table_anim', post.acf.table_anim);
    setAnimation('valid_anim', post.acf.valid_anim);
    setAnimation('group_anim', post.acf.group_anim);
    setAnimation('gift_anim', post.acf.gift_anim);
    setAnimation('print_anim', post.acf.print_anim);
  };

  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
  
    const delayDebounce = setTimeout(() => {
      if (searchTerm.length > 2) {
  
        // SHOW ALERT
        Swal.fire({
          title: 'Mencari Client',
          text: 'Mohon tunggu...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
  
        searchClients(searchTerm)
          .then((res) => {
            setSearchResults(res);
          })
          .catch((err) => {
            console.error('Search error:', err);
  
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'Gagal mencari client',
            });
          })
          .finally(() => {
            Swal.close(); // CLOSE ALERT
          });
  
      } else {
        setSearchResults([]);
      }
    }, 400);
  
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (!selectedPost) {
      setErrorMessage('Pilih Klien terlebih dahulu');
      setLoading(false);
      return;
    }

    try {
      const data = await loginResponse(username, password);
      const token = data.token;

      localStorage.setItem('token', token);

      const user = await getUser();
      const userRoles = user.roles;

      const matched = allowedRoles.find((role) => userRoles.includes(role));

      if (matched) {
        const isUsher = matched === 'usher';
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('name', username);
        localStorage.setItem('role', matched);
        // Customers see their Layar Sapa once before the dashboard.
        if (!isUsher) sessionStorage.setItem(LAYAR_SAPA_PENDING, '1');
        // Subscribers cannot scan, so send them straight to the dashboard.
        window.location.href = isUsher ? '/' : '/dashboard';
      } else {
        setErrorMessage(
          `Access denied: Only ${allowedRoles.join(' or ')} can log in`
        );
        localStorage.removeItem('token');
      }
    } catch (err) {
      const rawMsg =
        err?.response?.data?.message ||
        'Invalid username or password';
    
      const cleanMsg = rawMsg.replace(/<[^>]*>/g, '');
    
      setErrorMessage(cleanMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-page"
      style={{
        background: `#fff url(${LoginBg}) no-repeat`,
        backgroundSize: 'cover',
      }}
    >
      <div className="login_wrap">
        <div className="logo_wrap">
          <img src={Logo} alt="FairyTale" className="logo" />
        </div>

        {/* Search Client */}
        <div className="search-post">
          <input
            type="text"
            placeholder="CLIENT"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSelectedPost(null);
            }}
          />
          {searchResults.length > 0 && (
            <ul className="search-results">
              {searchResults.map((post) => (
                <li key={post.id} onClick={() => handleSelectPost(post)}>
                  {decodeHTML(post.title)}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Login Form */}
        <form className="login-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="USERNAME"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <div className="password-container">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="PASSWORD"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <span
              className="password-toggle-icon"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="#244361"
                  width="18"
                  height="18"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="#244361"
                  width="18"
                  height="18"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </span>
          </div>
          <button type="submit" disabled={loading}>
            <img className="usericon" src={userIcon} alt="usericon" />{' '}
            <span>{loading ? 'Logging in...' : 'LOGIN'}</span>
          </button>
        </form>

        {errorMessage && <p className="error">{errorMessage}</p>}
      </div>
    </div>
  );
}

export default Login;
